import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  ShieldCheck,
  Users,
  Building2,
  Activity,
  UserCheck,
  UserX,
  Clock,
  ArrowLeft,
  RefreshCw,
  Crown,
  Briefcase,
  LogIn
} from "lucide-react";

const BACKEND_URL = window.RUNTIME_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "";

export default function SuperAdminDashboard() {
  const { user, accessToken, logout } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  const apiCall = useCallback(async (endpoint) => {
    const res = await fetch(`${BACKEND_URL}${endpoint}`, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: "Request failed" }));
      throw new Error(err.detail || `Error ${res.status}`);
    }
    return res.json();
  }, [accessToken]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ov, us, biz, act] = await Promise.all([
        apiCall("/api/super-admin/overview"),
        apiCall("/api/super-admin/users"),
        apiCall("/api/super-admin/businesses"),
        apiCall("/api/super-admin/activity")
      ]);
      setOverview(ov);
      setUsers(us);
      setBusinesses(biz);
      setActivity(act);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => {
    if (user?.global_role !== "super_admin") {
      navigate("/dashboard");
      return;
    }
    loadData();
  }, [user, navigate, loadData]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const relativeTime = (dateStr) => {
    if (!dateStr) return "Never";
    const now = new Date();
    const d = new Date(dateStr);
    const diffMs = now - d;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return formatDate(dateStr);
  };

  const roleBadge = (role) => {
    const styles = {
      super_admin: "bg-purple-100 text-purple-800 border-purple-200",
      admin: "bg-blue-100 text-blue-800 border-blue-200",
      staff: "bg-green-100 text-green-800 border-green-200",
      pending: "bg-amber-100 text-amber-800 border-amber-200"
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${styles[role] || styles.pending}`} data-testid={`role-badge-${role}`}>
        {role === "super_admin" ? "Super Admin" : role?.charAt(0).toUpperCase() + role?.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950" data-testid="super-admin-loading">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading platform data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950" data-testid="super-admin-error">
        <Card className="max-w-md bg-slate-900 border-red-800">
          <CardContent className="pt-6 text-center">
            <ShieldCheck className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Access Denied</h2>
            <p className="text-slate-400 mb-4">{error}</p>
            <Button variant="outline" onClick={() => navigate("/dashboard")} className="border-slate-600 text-slate-300 hover:bg-slate-800">
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const admins = users.filter(u => u.global_role === "admin");
  const staffUsers = users.filter(u => u.global_role === "staff");

  const tabs = [
    { id: "overview", label: "System Overview", icon: Activity },
    { id: "admins", label: "Admin Accounts", icon: Crown },
    { id: "staff", label: "Staff Accounts", icon: Users },
    { id: "businesses", label: "Businesses", icon: Building2 },
    { id: "activity", label: "Activity Log", icon: Clock },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="super-admin-dashboard">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-600 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Accruey Super Admin</h1>
              <p className="text-xs text-slate-400">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={loadData} className="text-slate-400 hover:text-white hover:bg-slate-800" data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/"); }} className="text-slate-400 hover:text-white hover:bg-slate-800" data-testid="logout-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Tab Navigation */}
        <div className="flex gap-1 mb-6 overflow-x-auto pb-2 border-b border-slate-800">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? "bg-slate-800 text-purple-400 border-b-2 border-purple-500"
                  : "text-slate-400 hover:text-white hover:bg-slate-900"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && overview && (
          <div className="space-y-6" data-testid="section-overview">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard icon={Users} label="Total Users" value={overview.total_users} color="blue" testId="metric-total-users" />
              <MetricCard icon={UserCheck} label="Active (7d)" value={overview.active_users_7d} color="green" testId="metric-active-users" />
              <MetricCard icon={UserX} label="Inactive (30d)" value={overview.inactive_users_30d} color="red" testId="metric-inactive-users" />
              <MetricCard icon={Building2} label="Total Businesses" value={overview.total_businesses} color="purple" testId="metric-total-businesses" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <MetricCard icon={Crown} label="Admins" value={overview.total_admins} color="blue" testId="metric-admins" />
              <MetricCard icon={Users} label="Staff" value={overview.total_staff} color="green" testId="metric-staff" />
              <MetricCard icon={Clock} label="Pending Setup" value={overview.total_pending} color="amber" testId="metric-pending" />
            </div>

            {overview.top_admins_by_businesses.length > 0 && (
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-purple-400" />
                    Top Admins by Businesses Created
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {overview.top_admins_by_businesses.map((a, i) => (
                      <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-800/50" data-testid={`top-admin-${i}`}>
                        <span className="text-sm text-slate-300">{a.email}</span>
                        <span className="text-sm font-semibold text-purple-400">{a.business_count} businesses</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Admins Tab */}
        {activeTab === "admins" && (
          <div data-testid="section-admins">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white text-base">Admin Accounts ({admins.length})</CardTitle>
                <CardDescription className="text-slate-400">All users with admin role</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-700 text-slate-400">
                        <th className="text-left py-3 px-2">Name</th>
                        <th className="text-left py-3 px-2">Email</th>
                        <th className="text-left py-3 px-2">Businesses</th>
                        <th className="text-left py-3 px-2">Last Login</th>
                        <th className="text-left py-3 px-2">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {admins.map(u => (
                        <tr key={u.id} className="border-b border-slate-800 hover:bg-slate-800/50" data-testid={`admin-row-${u.email}`}>
                          <td className="py-3 px-2 text-white">{u.name || "—"}</td>
                          <td className="py-3 px-2 text-slate-300">{u.email}</td>
                          <td className="py-3 px-2 text-purple-400 font-medium">{u.businesses_created}</td>
                          <td className="py-3 px-2 text-slate-400">{relativeTime(u.last_login)}</td>
                          <td className="py-3 px-2 text-slate-500">{formatDate(u.created_at)}</td>
                        </tr>
                      ))}
                      {admins.length === 0 && (
                        <tr><td colSpan={5} className="py-8 text-center text-slate-500">No admin accounts yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Staff Tab */}
        {activeTab === "staff" && (
          <div data-testid="section-staff">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white text-base">Staff Accounts ({staffUsers.length})</CardTitle>
                <CardDescription className="text-slate-400">All users with staff role</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-700 text-slate-400">
                        <th className="text-left py-3 px-2">Name</th>
                        <th className="text-left py-3 px-2">Email</th>
                        <th className="text-left py-3 px-2">Businesses Assigned</th>
                        <th className="text-left py-3 px-2">Last Login</th>
                        <th className="text-left py-3 px-2">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {staffUsers.map(u => (
                        <tr key={u.id} className="border-b border-slate-800 hover:bg-slate-800/50" data-testid={`staff-row-${u.email}`}>
                          <td className="py-3 px-2 text-white">{u.name || "—"}</td>
                          <td className="py-3 px-2 text-slate-300">{u.email}</td>
                          <td className="py-3 px-2 text-green-400 font-medium">{u.businesses_accessed}</td>
                          <td className="py-3 px-2 text-slate-400">{relativeTime(u.last_login)}</td>
                          <td className="py-3 px-2 text-slate-500">{formatDate(u.created_at)}</td>
                        </tr>
                      ))}
                      {staffUsers.length === 0 && (
                        <tr><td colSpan={5} className="py-8 text-center text-slate-500">No staff accounts yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Businesses Tab */}
        {activeTab === "businesses" && (
          <div data-testid="section-businesses">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white text-base">All Businesses ({businesses.length})</CardTitle>
                <CardDescription className="text-slate-400">Business creation activity across the platform</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-700 text-slate-400">
                        <th className="text-left py-3 px-2">Business Name</th>
                        <th className="text-left py-3 px-2">Created By</th>
                        <th className="text-left py-3 px-2">Staff Members</th>
                        <th className="text-left py-3 px-2">Drive Linked</th>
                        <th className="text-left py-3 px-2">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {businesses.map(b => (
                        <tr key={b.id} className="border-b border-slate-800 hover:bg-slate-800/50" data-testid={`business-row-${b.name}`}>
                          <td className="py-3 px-2 text-white font-medium">{b.name}</td>
                          <td className="py-3 px-2 text-slate-300">{b.created_by}</td>
                          <td className="py-3 px-2 text-blue-400">{b.staff_count}</td>
                          <td className="py-3 px-2">
                            {b.drive_folder_id
                              ? <span className="text-green-400 text-xs">Linked</span>
                              : <span className="text-amber-400 text-xs">Not linked</span>
                            }
                          </td>
                          <td className="py-3 px-2 text-slate-500">{formatDate(b.created_at)}</td>
                        </tr>
                      ))}
                      {businesses.length === 0 && (
                        <tr><td colSpan={5} className="py-8 text-center text-slate-500">No businesses created yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === "activity" && activity && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="section-activity">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <LogIn className="w-4 h-4 text-blue-400" />
                  Recent Logins
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {activity.recent_logins.map((l, i) => (
                    <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-800/50" data-testid={`login-activity-${i}`}>
                      <div>
                        <p className="text-sm text-white">{l.name || l.email}</p>
                        <p className="text-xs text-slate-500">{l.email}</p>
                      </div>
                      <div className="text-right">
                        {roleBadge(l.role)}
                        <p className="text-xs text-slate-500 mt-1">{relativeTime(l.last_login)}</p>
                      </div>
                    </div>
                  ))}
                  {activity.recent_logins.length === 0 && (
                    <p className="text-center text-slate-500 py-4">No login activity</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-purple-400" />
                  Recent Business Creation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {activity.recent_businesses.map((b, i) => (
                    <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-800/50" data-testid={`business-activity-${i}`}>
                      <div>
                        <p className="text-sm text-white font-medium">{b.name}</p>
                        <p className="text-xs text-slate-500">by {b.created_by}</p>
                      </div>
                      <p className="text-xs text-slate-500">{relativeTime(b.created_at)}</p>
                    </div>
                  ))}
                  {activity.recent_businesses.length === 0 && (
                    <p className="text-center text-slate-500 py-4">No businesses created</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, color, testId }) {
  const colors = {
    blue: "bg-blue-500/10 text-blue-400 border-blue-900/50",
    green: "bg-green-500/10 text-green-400 border-green-900/50",
    red: "bg-red-500/10 text-red-400 border-red-900/50",
    purple: "bg-purple-500/10 text-purple-400 border-purple-900/50",
    amber: "bg-amber-500/10 text-amber-400 border-amber-900/50"
  };

  return (
    <Card className={`bg-slate-900 border-slate-800`} data-testid={testId}>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-2xl font-bold text-white">{value}</p>
            <p className="text-xs text-slate-400">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
